import { clsx } from "clsx";
import { clone } from "ramda";
import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { HStack, Spacer, VStack } from "widgets";
import { useURLSearchParam } from "widgets/hooks";
import type { StoreApi, UseBoundStore } from "zustand";
import { shallow } from "zustand/shallow";

import { genericFilter } from "@/app-utils/filter.ts";
import { createSelectionModel, type ISelectionModel } from "@/app-utils/selection-model.ts";
import { type Dictionary } from "@/app-utils/type.ts";
import { type IndexStatus, type ProjectFile } from "@/graphql.ts";
import { selMessage } from "@/messages/message.ts";
import type { BrowsingProjectFileFragment } from "@/modules/files/graphql/files-queries.gql.ts";
import { fileBrowserGridColumnDefs } from "@/modules/files/settings/column-defs.ts";
import style from "@/modules/files/styles/FileBrowser.module.css";
import { isBrowseable } from "@/modules/files/utils/file.ts";
import { defaultFileBrowserSorting } from "@/modules/files/utils/utils.ts";
import { type IFileState } from "@/redux/modules/files.ts";
import type { IIdentifiable } from "@/redux/modules/table-data.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { FILE_BROWSE_TYPES } from "@/settings/settings.ts";
import { ContentPlaceholder } from "@/ui-modules/core/ContentPlaceholder.tsx";

import { AsyncSearchWithExprParser } from "../../../ui-modules/core/forms/AsyncSearchWithExprParser.tsx";
import DataContextIndicator from "../../../ui-modules/core/grid/DataContextIndicator.tsx";
import InMemoryTableWithSelectionModel from "../../../ui-modules/core/grid/InMemoryTableWithSelectionModel.tsx";
import BamFileValidationDialog from "../../../ui-modules/files/BamFileValidationDialog.tsx";
import { FileBrowserActionWidgets } from "../../../ui-modules/files/FileBrowserActionWidgets.tsx";
import { FileBrowserSortingWidget } from "../../../ui-modules/files/FileBrowserSortingWidget.tsx";
import { FileUpload } from "../../../ui-modules/files/FileUpload.tsx";
import { notify } from "../../notifications/notify.tsx";
import { useFileBrowserHotKeys } from "../hooks/useFileBrowserHotKeys.ts";
import type { FileBrowserStore } from "../stores/file-browser.ts";
import { BreadcrumbButton } from "./BreadcrumbButton.tsx";
import { FileBrowserBreadcrumbWidget } from "./FileBrowserBreadcrumbWidget.tsx";

export interface FileBrowserProps {
  initialFolderId: number;
  useStore: UseBoundStore<StoreApi<FileBrowserStore>>;
  singleSelection?: boolean; // single/multi selection
  isSelectableFilter?: (file: BrowsingProjectFileFragment) => boolean;
  selectableFileTypes?: FILE_BROWSE_TYPES[];
  className?: string;
}

export interface IdentifiableFileData extends IIdentifiable {
  uuid: string;
  filesToBeMoved?: BrowsingProjectFileFragment[] | null;
  file: BrowsingProjectFileFragment;
  refresh?: (triggerFileScanner?: boolean) => Promise<void>;
}

// TODO Deselect everything on unmount + new fetch

/* --- Grid Component --- */
export const FileBrowser: FC<FileBrowserProps> = (props) => {
  const { load, refresh, select, files, loading, currentFolder, selectedFiles, error } = props.useStore(
    (state: FileBrowserStore) => state,
    shallow,
  );

  const [filter, setFilter] = useState<string>();
  const [sorting, setSorting] = useState<string>("file");

  const [dropZoneOpenCallback, setDropZoneOpenCallback] = useState<() => void>();

  const [fileRows, setFileRows] = useState<IdentifiableFileData[]>([]); // type defined in buildFileRowData

  const dataTableRef = useRef<InMemoryTableWithSelectionModel>(null);
  // Only still used for copy/cut/paste/upload
  const filesState = useSelector<IAppState, IFileState>((state) => state.files, shallowEqual);
  const indexingStatusLookup = useSelector<IAppState, Dictionary<IndexStatus>>(
    (state) => state.files.indexingStatusLookup,
  );
  const targetFile = useURLSearchParam("file");

  useEffect(() => {
    if (!targetFile) {
      dataTableRef.current?.resetSelection();
    }
  }, [currentFolder, targetFile]);

  useEffect(() => {
    if (props.initialFolderId) {
      if (!files || !currentFolder || props.initialFolderId !== currentFolder.id) {
        void load(props.initialFolderId);
      }
    } else {
      notify.error(
        {
          title: "File Browser Error",
          children: "This project seems to not have a folder attached to it. Please contact your administrator.",
        },
        { autoClose: false },
      );
      // reset
      props.useStore.setState({
        loading: false,
        files: null,
        error: new Error("This project seems to not have a folder attached to it"),
      });
    }
  }, [props.initialFolderId]);

  // Keep the shortcuts local
  const hotKeysRef = useRef<HTMLDivElement>(null);
  useFileBrowserHotKeys(hotKeysRef, props.useStore, dataTableRef);

  const isFileTypeSelectable = useCallback(
    (fileType: FILE_BROWSE_TYPES) =>
      props.selectableFileTypes
        ? props.selectableFileTypes.includes(fileType)
        : ![FILE_BROWSE_TYPES.PROJECT, FILE_BROWSE_TYPES.PROJECT_GROUP].includes(fileType),
    [props.selectableFileTypes],
  );

  const checkIfSelectable = useCallback(
    (file: BrowsingProjectFileFragment) => {
      const filter = props.isSelectableFilter;
      return typeof filter === "function" ? filter(file) : true;
    },
    [props.isSelectableFilter],
  );

  // Create the data for the Table
  useEffect(() => {
    const children = clone(files);
    const rows = children
      ?.sort(defaultFileBrowserSorting)
      .map((file) => {
        const isSelectable = isFileTypeSelectable(file.fileBrowseType as FILE_BROWSE_TYPES) && checkIfSelectable(file);
        return {
          uuid: String(file.id),
          id: file.id,
          isSelectable,
          filesToBeMoved: filesState.filesToCut,
          file,
          refresh,
        } satisfies IdentifiableFileData;
      })
      .filter((file) => genericFilter(fileBrowserGridColumnDefs, file, filter));
    setFileRows(rows ?? []);
  }, [checkIfSelectable, files, filesState.filesToCut, isFileTypeSelectable, filter]);

  // refresh the file browser current location if the latest upload corresponds to this same location
  useEffect(() => {
    if (
      filesState.latestCompletedUpload != undefined &&
      currentFolder?.id != undefined &&
      filesState.latestCompletedUpload.targetLocation === currentFolder.humanReadablePath
    ) {
      void refresh();
    }
  }, [filesState.latestCompletedUpload]);

  const [projectFilesForGenomeViewer, setProjectFilesForGenomeViewer] = useState<ProjectFile[] | null>(null);

  const showUploadValidationError = useCallback((message: string) => {
    // message can be null in order to reset the error (other scenarios like message in the form footer)
    if (message) {
      notify.error({
        title: selMessage({ id: "filesPage.uploadValidation.errorTitle" }),
        children: message,
      });
    }
  }, []);

  return (
    <>
      <VStack spacing={0} style={{ width: "100%", height: "100%", background: "var(--light-background)" }}>
        <HStack className={style.toolbar} data-testid="file-browser-header">
          <FileBrowserBreadcrumbWidget useStore={props.useStore} key={"files-page-action-breadcrumb"} />
          <BreadcrumbButton useStore={props.useStore} key={"file-picker-action-breadcrumb-button"} />
          <FileBrowserSortingWidget
            sorting={sorting}
            setSorting={setSorting}
            key={"file-picker-action-sorting-widget"}
          />
          <AsyncSearchWithExprParser
            key={`files-page-action-search-${currentFolder?.id}`}
            onSearchExprChange={async (expr) => setFilter(expr)}
          />
          <Spacer />
          <FileBrowserActionWidgets
            refreshCurrentFolder={refresh}
            currentFolder={currentFolder}
            selectedFiles={selectedFiles}
            resetSelection={dataTableRef.current?.resetSelection}
            handleClickUploadButton={() => dropZoneOpenCallback?.()}
            key={`files-page-action-selectedfiles-${currentFolder?.id}`}
          />
        </HStack>
        <div style={{ position: "relative", flex: "1 1 auto", display: "flex", overflow: "hidden" }} ref={hotKeysRef}>
          {error ? (
            <ContentPlaceholder>An error occurred.</ContentPlaceholder>
          ) : (
            <FileUpload
              setDropZoneOpenCallback={(cb) => setDropZoneOpenCallback(() => cb)}
              notAuthorized={!currentFolder?.isEditable}
              setUploadValidationError={showUploadValidationError}
              currentFolder={currentFolder}
            >
              <InMemoryTableWithSelectionModel
                key={"file-browser"}
                ref={dataTableRef}
                scrollToId={targetFile}
                noRowFlash
                className={clsx("file-browser", props.className)}
                isLoading={loading}
                uniqueGridIdentifier="file-browser"
                columns={fileBrowserGridColumnDefs}
                rowGridData={fileRows}
                useSelectionModel
                allowRowSelection={false} // not compatible with the folder selection ! to be consistent let's forbid the usage of row click selection in file picker
                selectionModelUpdated={selectionModelUpdated}
                gridProps={{
                  onCellDoubleClicked,
                  //immutableData: true, // allow to change the row nodes without reloading completely the grid, but requires to manage the scroll to top programmatically! the timing between loading and scroll to top is hard to manage, let's avoid this for now in favor of programmatic updates (cell rendering + all places where needed)
                  getRowNodeId: (data) => data.id,
                }}
                noRowsMessage={filter ? "Filter returns no match" : "Empty folder"}
                sortingField={sorting ?? ""}
                onSortModified={(sortedColumns) => {
                  const mainSortingField = sortedColumns.find((sortedColumn) => sortedColumn.sort?.priority === 1);
                  setSorting(mainSortingField?.field);
                }}
                exclusiveSelection={props.singleSelection}
                initialSelectionModel={targetFile ? createSelectionModel([Number(targetFile)]) : undefined}
              />
            </FileUpload>
          )}
          {!loading && (
            <DataContextIndicator
              key="context-indicator"
              numTotalRows={files?.length ?? 0}
              numRowsFiltered={fileRows.length}
              numRowsSelected={selectedFiles.length}
              rowEntityLabel={"item"}
              filterIsSet={!!filter?.length}
            />
          )}
        </div>
      </VStack>

      {projectFilesForGenomeViewer && (
        <BamFileValidationDialog
          key="file-bam-validation-dialog"
          projectFiles={projectFilesForGenomeViewer}
          onFileAssociationsUpdated={void refresh}
          onClose={() => setProjectFilesForGenomeViewer(null)}
        />
      )}
    </>
  );

  function selectionModelUpdated(selectionModel: ISelectionModel) {
    const selectedFiles: BrowsingProjectFileFragment[] = selectionModel.selectedIds
      .map((id) => files?.find((file) => file.id === id))
      .filter((file) => !!file)
      .map((file) => {
        return {
          ...file,
          indexingStatus: indexingStatusLookup[file.id] || file.indexingStatus,
        } satisfies BrowsingProjectFileFragment;
      });
    select(selectedFiles);
  }

  function onCellDoubleClicked(event) {
    if (
      isBrowseable(event.data.file.fileBrowseType) &&
      !filesState.filesToCut?.find((f: ProjectFile) => f.id === event.data.file.id)
    ) {
      load(event.data.file.id);
    }
  }
};
